import { useState } from "react";
import { CalendarDays } from "lucide-react";
import { format, parseISO, startOfDay } from "date-fns";
import { it, enGB } from "date-fns/locale";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { useLang } from "@/context/LanguageContext";

const toIso = (d) => format(d, "yyyy-MM-dd");
const fromIso = (s) => (s ? parseISO(s) : undefined);

export const DateRangeField = ({ checkin, checkout, onChange, testid, className = "", compact = false }) => {
  const { t, lang } = useLang();
  const [open, setOpen] = useState(false);
  const [hover, setHover] = useState(null);
  const from = fromIso(checkin);
  const to = fromIso(checkout);
  const locale = lang === "en" ? enGB : it;
  const picking = from && !to;

  const onDayClick = (day, mods) => {
    if (mods.disabled) return;
    if (!from || to || day <= from) { onChange(toIso(day), ""); return; }
    onChange(checkin, toIso(day));
    setHover(null);
    setOpen(false);
  };

  const fmt = (d) => format(d, compact ? "d MMM" : "d MMM yyyy", { locale });
  const label = from ? `${fmt(from)} → ${to ? fmt(to) : "…"}` : t("home.wDatesPh");
  const nights = from && to ? Math.round((to - from) / 86400000) : 0;

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button type="button" data-testid={testid} className={`w-full flex items-center gap-2 text-left border border-brand-plum/15 bg-white text-brand-plum focus:outline-none focus:border-brand-teal ${className}`}>
          <CalendarDays size={compact ? 14 : 16} className="text-brand-teal shrink-0" />
          <span className={`truncate ${from ? "" : "text-brand-plum/45"}`}>{label}</span>
          {nights > 0 && !compact && <span className="ml-auto text-xs text-brand-plum/50 shrink-0">{nights} {t("home.wNights")}</span>}
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-0 z-[60]" align="start" side={compact ? "top" : "bottom"} data-testid={`${testid}-popover`}>
        <p className="px-4 pt-3 text-xs font-semibold text-brand-tealDark" data-testid={`${testid}-hint`}>
          {picking ? t("home.wPickOut") : t("home.wPickIn")}
        </p>
        <Calendar
          mode="range"
          locale={locale}
          numberOfMonths={compact ? 1 : 2}
          defaultMonth={from || new Date()}
          selected={{ from, to: to || (picking && hover > from ? hover : undefined) }}
          onDayClick={onDayClick}
          onDayMouseEnter={(d) => picking && setHover(d)}
          disabled={{ before: startOfDay(new Date()) }}
        />
      </PopoverContent>
    </Popover>
  );
};
